'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowRight, Wrench, Handshake, Building2, Users, Sparkles, Newspaper, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSiteSettings } from '@/lib/hooks/useSiteSettings';
interface Pillar {
  title: string;
  description: string;
  href: string;
  buttonLabel: string;
  accent: {
    glow: string;
    border: string;
    button: string;
  };
}
const PILLARS: Pillar[] = [{
  title: 'Create and Convert Demand',
  description: 'Build awareness, drive trial, convert to purchase, and measure what works. Demand Central brings the full funnel into one place.',
  href: '/demand',
  buttonLabel: 'Become a Demand Master and Make Your Next Moves',
  accent: {
    glow: 'from-primary-500/25 via-orange-500/10 to-transparent',
    border: 'group-hover:border-primary-400/50',
    button: 'from-primary-600 to-primary-500'
  }
}, {
  title: 'Global Demand: Export to the World',
  description: 'Work out whether to export, find the right markets, build a plan, and execute with in-market experts and partners alongside you.',
  href: '/export',
  buttonLabel: 'Export Central',
  accent: {
    glow: 'from-blue-500/25 via-cyan-500/10 to-transparent',
    border: 'group-hover:border-blue-400/50',
    button: 'from-blue-600 to-blue-500'
  }
}];

/** Supporting paths that serve both pillars */
const SUPPORTING_LINKS = [
// {
//   icon: Wrench,
//   label: 'Tools and Resources',
//   href: '/tools',
// },
{
  icon: Handshake,
  label: 'Opportunities',
  href: '/opportunities'
}, {
  icon: Building2,
  label: 'Directory',
  href: '/directory'
}, {
  icon: Users,
  label: 'Collaborations Central',
  href: '/content/collaborations-central'
}, {
  icon: Sparkles,
  label: 'Master Demand 101',
  href: '/demand'
}

// {
//   icon: Users,
//   label: 'Hubs',
//   href: '/hubs',
// },
// {
//   icon: Newspaper,
//   label: 'News',
//   href: '/content',
// },
];
function PillarCard(t0) {
  const $ = _c(23);
  const {
    title,
    description,
    href,
    buttonLabel,
    accent
  } = t0;
  let t1;
  if ($[0] !== accent.border) {
    t1 = cn("group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-800/60 p-8 transition-all duration-300 sm:p-10", "hover:-translate-y-1.5 hover:bg-gray-800 hover:shadow-2xl hover:shadow-black/40", accent.border);
    $[0] = accent.border;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== accent.glow) {
    t2 = cn("pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-gradient-to-br opacity-70 blur-3xl transition-opacity duration-300 group-hover:opacity-100", accent.glow);
    $[2] = accent.glow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span aria-hidden="true" className={t2} />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== title) {
    t4 = <h3 className="relative text-2xl font-extrabold tracking-tight text-white sm:text-3xl">{title}</h3>;
    $[6] = title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== description) {
    t5 = <p className="relative mt-4 flex-1 text-base leading-relaxed text-gray-300 sm:text-lg sm:leading-8">{description}</p>;
    $[8] = description;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== accent.button) {
    t6 = cn("relative mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r px-6 py-3.5 text-base font-bold text-white transition-all duration-300 group-hover:brightness-110 sm:text-lg", accent.button);
    $[10] = accent.button;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== buttonLabel || $[14] !== t6) {
    t8 = <span className={t6}>{buttonLabel}{t7}</span>;
    $[13] = buttonLabel;
    $[14] = t6;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== href || $[17] !== t1 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t8) {
    t9 = <Link href={href} className={t1}>{t3}{t4}{t5}{t8}</Link>;
    $[16] = href;
    $[17] = t1;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}
interface FinalCTASectionV3Props {
  demandOnly?: boolean;
}
export function FinalCTASectionV3(t0) {
  const $ = _c(10);
  const {
    demandOnly: t1
  } = t0;
  const demandOnly = t1 === undefined ? false : t1;
  demandOnly ? PILLARS.slice(0, 1) : PILLARS;
  useSiteSettings();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden="true" className="absolute right-0 top-0 h-80 w-80 translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-600/20 blur-3xl" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = demandOnly ? "bg-orange-500/15" : "bg-blue-600/15";
  let t4;
  if ($[1] !== t3) {
    t4 = cn("absolute bottom-0 left-0 h-80 w-80 -translate-x-1/2 translate-y-1/2 rounded-full blur-3xl", t3);
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t4) {
    t5 = <div aria-hidden="true" className={t4} />;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mx-auto mb-12 max-w-3xl text-center sm:mb-14"><h2 className="text-3xl font-black tracking-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">Start Now</h2></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex justify-center"><Link href="/inspiration-feed" className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#ff5a36] px-7 py-3.5 text-base font-black text-white shadow-lg shadow-red-500/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-red-500/30">Start with Inspiration and then Make Your Next Moves<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link></div>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="relative z-10">{t6}{t7}<div className="mt-10 flex flex-col items-center gap-4 border-t border-white/10 pt-8 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-8 sm:gap-y-4">{SUPPORTING_LINKS.map(_temp)}{null && <Link href={`/content/${null.slug}`} className="inline-flex cursor-pointer items-center gap-2 text-base font-semibold text-gray-300 transition-colors hover:text-white"><Calendar className="h-5 w-5 text-gray-400" aria-hidden="true" />Office Hours<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>}</div></div>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t5) {
    t9 = <section className="py-20 lg:py-28"><div className="container mx-auto px-4 sm:px-6 lg:px-8"><div className="relative overflow-hidden rounded-3xl bg-gray-900 p-8 sm:p-12 md:p-16">{t2}{t5}{t8}</div></div></section>;
    $[8] = t5;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function _temp(t0) {
  const {
    icon: Icon,
    label,
    href
  } = t0;
  return <Link key={href} href={href} className="inline-flex cursor-pointer items-center gap-2 text-base font-semibold text-gray-300 transition-colors hover:text-white"><Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />{label}<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>;
}